import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { KPICard } from '../../components/ui/KPICard';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { purchaseOrders, poLineItems, PurchaseOrder, POLineItem } from '../../data/orders';
import { ShoppingCartIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, ArrowLeftIcon, DownloadIcon, PrinterIcon, MessageSquareIcon, FileTextIcon } from 'lucide-react';
export const OrdersPage: React.FC = () => {
  const {
    user
  } = useAuth();
  // In a real app, we'd filter by the logged-in supplier's ID.
  // For demo, we'll use SUP-001 (Oceanic Harvest) as the default if not specified.
  const supplierId = 'SUP-001';
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  // Filter orders for this supplier
  const myOrders = useMemo(() => {
    return purchaseOrders.filter(po => po.supplierId === supplierId);
  }, [supplierId]);
  // Filter based on active tab
  const filteredOrders = useMemo(() => {
    if (activeTab === 'all') return myOrders;
    if (activeTab === 'action-required') return myOrders.filter(po => ['issued', 'change-requested'].includes(po.status));
    if (activeTab === 'active') return myOrders.filter(po => ['acknowledged', 'partially-confirmed', 'confirmed', 'in-transit', 'partially-received'].includes(po.status));
    if (activeTab === 'completed') return myOrders.filter(po => ['received', 'closed'].includes(po.status));
    return myOrders;
  }, [myOrders, activeTab]);
  // KPIs
  const actionRequiredCount = myOrders.filter(po => ['issued', 'change-requested'].includes(po.status)).length;
  const activeCount = myOrders.filter(po => ['acknowledged', 'partially-confirmed', 'confirmed', 'in-transit', 'partially-received'].includes(po.status)).length;
  const totalValue = myOrders.reduce((sum, po) => sum + po.totalAmount, 0);
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Detail View
  if (selectedOrder) {
    const lineItems = poLineItems.filter(li => li.poId === selectedOrder.id);
    return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-1478">
        <div className="flex items-center gap-2 text-sm text-text-secondary mb-4 cursor-pointer hover:text-fpg-navy transition-colors" onClick={() => setSelectedOrder(null)} data-id="element-1479">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1480" />
          Back to Purchase Orders
        </div>

        <PageHeader title={`Purchase Order: ${selectedOrder.id}`} description={`Issued on ${selectedOrder.orderDate} by ${selectedOrder.buyerName} (${selectedOrder.department})`} primaryAction={['issued', 'change-requested'].includes(selectedOrder.status) ? <Button variant="primary" leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1483" />} data-id="element-1482">
                Acknowledge Order
              </Button> : <Button variant="secondary" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-1485" />} data-id="element-1484">
                Create Invoice
              </Button>} secondaryActions={[{
        label: 'Download PDF',
        icon: <DownloadIcon className="w-4 h-4" data-id="element-1486" />,
        onClick: () => {}
      }, {
        label: 'Print',
        icon: <PrinterIcon className="w-4 h-4" data-id="element-1487" />,
        onClick: () => {}
      }, {
        label: 'Contact Buyer',
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-1488" />,
        onClick: () => {}
      }]} data-id="element-1481" />

        {['issued', 'change-requested'].includes(selectedOrder.status) && <div className="bg-status-warning-bg border border-status-warning/20 rounded-fluent-lg p-4 flex items-start gap-3" data-id="element-1489">
            <AlertCircleIcon className="w-5 h-5 text-status-warning mt-0.5 flex-shrink-0" data-id="element-1490" />
            <div data-id="element-1491">
              <h3 className="text-sm font-semibold text-status-warning mb-1" data-id="element-1492">
                Action Required
              </h3>
              <p className="text-sm text-text-secondary" data-id="element-1493">
                This order requires your acknowledgement by{' '}
                {selectedOrder.acknowledgementDue || 'the requested date'}.
                Please review the line items and confirm you can meet the
                delivery requirements.
              </p>
              <div className="mt-3 flex gap-3" data-id="element-1494">
                <Button variant="primary" size="sm" data-id="element-1495">
                  Acknowledge
                </Button>
                <Button variant="secondary" size="sm" data-id="element-1496">
                  Propose Changes
                </Button>
                <Button variant="ghost" size="sm" className="text-status-error hover:text-status-error hover:bg-status-error-bg" data-id="element-1497">
                  Reject Order
                </Button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1498">
          <Card className="md:col-span-2 space-y-6" data-id="element-1499">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-1500">
              Order Details
            </h2>

            <div className="grid grid-cols-2 gap-y-6 gap-x-4" data-id="element-1501">
              <div data-id="element-1502">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1503">
                  Status
                </p>
                <StatusPill status={selectedOrder.status} data-id="element-1504" />
              </div>
              <div data-id="element-1505">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1506">
                  Total Amount
                </p>
                <p className="text-lg font-bold text-text-primary" data-id="element-1507">
                  {formatCurrency(selectedOrder.totalAmount, selectedOrder.currency)}
                </p>
              </div>
              <div data-id="element-1508">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1509">
                  Order Date
                </p>
                <p className="text-sm text-text-primary" data-id="element-1510">
                  {selectedOrder.orderDate}
                </p>
              </div>
              <div data-id="element-1511">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1512">
                  Required Delivery Date
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1513">
                  {selectedOrder.deliveryDate}
                </p>
              </div>
              <div className="col-span-2" data-id="element-1514">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1515">
                  Delivery Address
                </p>
                <p className="text-sm text-text-primary" data-id="element-1516">
                  {selectedOrder.deliveryAddress}
                </p>
              </div>
              <div data-id="element-1517">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1518">
                  Incoterms
                </p>
                <p className="text-sm text-text-primary" data-id="element-1519">
                  {selectedOrder.incoterms}
                </p>
              </div>
              <div data-id="element-1520">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1521">
                  Urgency
                </p>
                <Badge variant={selectedOrder.urgency === 'urgent' ? 'warning' : selectedOrder.urgency === 'critical' ? 'error' : 'neutral'} data-id="element-1522">
                  {selectedOrder.urgency.charAt(0).toUpperCase() + selectedOrder.urgency.slice(1)}
                </Badge>
              </div>
            </div>
          </Card>

          <Card className="space-y-6" data-id="element-1523">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-1524">
              Summary
            </h2>
            <div className="space-y-3" data-id="element-1525">
              <div className="flex justify-between text-sm" data-id="element-1526">
                <span className="text-text-secondary" data-id="element-1527">
                  Subtotal ({selectedOrder.lineItems} items)
                </span>
                <span className="text-text-primary font-medium" data-id="element-1528">
                  {formatCurrency(selectedOrder.totalAmount * 0.93, selectedOrder.currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1529">
                <span className="text-text-secondary" data-id="element-1530">Tax (7% GST)</span>
                <span className="text-text-primary font-medium" data-id="element-1531">
                  {formatCurrency(selectedOrder.totalAmount * 0.07, selectedOrder.currency)}
                </span>
              </div>
              <div className="pt-3 border-t border-border-light flex justify-between" data-id="element-1532">
                <span className="text-base font-semibold text-text-primary" data-id="element-1533">
                  Total
                </span>
                <span className="text-base font-bold text-text-primary" data-id="element-1534">
                  {formatCurrency(selectedOrder.totalAmount, selectedOrder.currency)}
                </span>
              </div>
            </div>
          </Card>
        </div>

        <Card noPadding data-id="element-1535">
          <div className="p-5 border-b border-border-light flex justify-between items-center" data-id="element-1536">
            <h2 className="text-lg font-semibold text-text-primary" data-id="element-1537">
              Line Items
            </h2>
            <Badge variant="neutral" data-id="element-1538">{lineItems.length} Items</Badge>
          </div>
          <DataTable data={lineItems} columns={[{
          header: 'Line',
          accessorKey: 'lineNo',
          width: '60px'
        }, {
          header: 'Item Code',
          accessorKey: 'materialCode'
        }, {
          header: 'Description',
          accessorKey: 'description'
        }, {
          header: 'Qty',
          align: 'right',
          cell: row => <span className="font-medium" data-id="element-1540">
                    {row.quantity} {row.unit}
                  </span>
        }, {
          header: 'Unit Price',
          align: 'right',
          cell: row => formatCurrency(row.unitPrice, selectedOrder.currency)
        }, {
          header: 'Total',
          align: 'right',
          cell: row => <span className="font-medium" data-id="element-1541">
                    {formatCurrency(row.totalPrice, selectedOrder.currency)}
                  </span>
        }, {
          header: 'Status',
          cell: row => <StatusPill status={row.status} data-id="element-1542" />
        }]} data-id="element-1539" />
        </Card>
      </div>;
  }
  // List View
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-1543">
      <PageHeader title="Purchase Orders" description="Manage and acknowledge purchase orders from FairPrice Group." breadcrumbs={[{
      label: 'Home',
      href: '/supplier/dashboard'
    }, {
      label: 'Orders'
    }]} primaryAction={<Button variant="primary" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1546" />} data-id="element-1545">
            Export List
          </Button>} data-id="element-1544" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1547">
        <KPICard title="Action Required" value={actionRequiredCount.toString()} icon={<AlertCircleIcon className="w-5 h-5 text-status-warning" data-id="element-1549" />} trend={actionRequiredCount > 0 ? 'up' : 'neutral'} trendValue={actionRequiredCount > 0 ? 'Needs attention' : 'All caught up'} data-id="element-1548" />
        <KPICard title="Active Orders" value={activeCount.toString()} icon={<ShoppingCartIcon className="w-5 h-5 text-fpg-navy" data-id="element-1551" />} data-id="element-1550" />
        <KPICard title="Total Order Value" value={formatCurrency(totalValue, 'SGD')} icon={<CheckCircleIcon className="w-5 h-5 text-status-success" data-id="element-1553" />} data-id="element-1552" />
      </div>

      <Card noPadding className="flex flex-col overflow-hidden" data-id="element-1554">
        <div className="px-6 pt-4 border-b border-border-light" data-id="element-1555">
          <Tabs tabs={[{
          id: 'all',
          label: 'All Orders',
          count: myOrders.length
        }, {
          id: 'action-required',
          label: 'Action Required',
          count: actionRequiredCount
        }, {
          id: 'active',
          label: 'Active',
          count: activeCount
        }, {
          id: 'completed',
          label: 'Completed'
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-1556" />
        </div>

        <DataTable data={filteredOrders} onRowClick={row => setSelectedOrder(row)} columns={[{
        header: 'PO Number',
        accessorKey: 'id',
        sortable: true
      }, {
        header: 'Order Date',
        accessorKey: 'orderDate',
        sortable: true
      }, {
        header: 'Delivery Date',
        accessorKey: 'deliveryDate',
        sortable: true
      }, {
        header: 'Amount',
        align: 'right',
        sortable: true,
        cell: row => <span className="font-medium" data-id="element-1558">
                  {formatCurrency(row.totalAmount, row.currency)}
                </span>
      }, {
        header: 'Items',
        accessorKey: 'lineItems',
        align: 'center'
      }, {
        header: 'Status',
        cell: row => <StatusPill status={row.status} data-id="element-1559" />
      }]} data-id="element-1557" />
      </Card>
    </div>;
};